import type { OnChanges, SimpleChanges } from '@angular/core';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { convertTextToNativeEmojis } from '@freelancer/pipes';
import { LinkColor, LinkHoverColor } from '@freelancer/ui/link';
import {
  FontColor,
  FontType,
  FontWeight,
  ReadMore,
  ReadMoreColor,
  TextSize,
} from '@freelancer/ui/text';

/**
 * A component for converting links and/or emojis within a text block into actual links/emojis.
 * Can take in (some) inputs that fl-text or fl-link can take in.
 */
@Component({
  selector: 'fl-interactive-text',
  template: `
    <ng-container *ngIf="!rawOutput; else rawContent">
      <ng-container *ngIf="link; else plainText">
        <fl-text
          *flAutoLink="true; color: linkColor; hoverColor: linkHoverColor"
          [color]="fontColor"
          [displayLineBreaks]="displayLineBreaks"
          [fontType]="FontType.SPAN"
          [readMore]="readMore"
          [readMoreColor]="readMoreColor"
          [size]="fontSize"
          [weight]="fontWeight"
          [maxLines]="maxLines"
        >
          {{ text }}
        </fl-text>
      </ng-container>

      <ng-template #plainText>
        <fl-text
          [color]="fontColor"
          [displayLineBreaks]="displayLineBreaks"
          [fontType]="FontType.SPAN"
          [readMore]="readMore"
          [readMoreColor]="readMoreColor"
          [size]="fontSize"
          [weight]="fontWeight"
          [maxLines]="maxLines"
        >
          {{ text }}
        </fl-text>
      </ng-template>
    </ng-container>

    <ng-template #rawContent>
      {{ text }}
    </ng-template>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InteractiveTextComponent implements OnChanges {
  FontType = FontType;
  LinkHoverColor = LinkHoverColor;

  @Input() content: string;
  @Input() linkHoverColor: LinkHoverColor = LinkHoverColor.INHERIT;
  @Input() linkColor: LinkColor = LinkColor.DEFAULT;
  @Input() fontColor: FontColor = FontColor.FOREGROUND;
  @Input() fontSize: TextSize = TextSize.XXSMALL;
  @Input() fontWeight: FontWeight = FontWeight.NORMAL;
  @Input() link = false;
  @Input() emoji = false;
  @Input() displayLineBreaks = true;
  @Input() maxLines?: number;
  @Input() readMore = ReadMore.NONE;
  @Input() readMoreColor?: ReadMoreColor;
  /** Return only the text content, not wrapped in a fl-link or fl-text. */
  @Input() rawOutput = false;

  text: string;

  ngOnChanges(changes: SimpleChanges): void {
    if ('content' in changes) {
      this.text = this.replaceEmojis(this.content);
    }
  }

  replaceEmojis(text: string): string {
    if (!this.emoji) {
      return text;
    }

    return convertTextToNativeEmojis(text);
  }
}
